/* Sprint 5 — KPI donut tiles for the Dashboard.
 *
 * Three side-by-side circular charts:
 *   1. Total projects donut — "X of Y complete" with completion %
 *      at the centre, segments coloured by status
 *   2. Per-stage progress — three small donuts (DD / SPA / Risk)
 *      showing how many projects have each stage running vs done
 *   3. (optional) Flagged tile when any project is flagged
 *
 * Pulls /projects?status=... in parallel to derive counts, then renders
 * SVG circles via stroke-dasharray. No charting library dependency.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getProject, listProjects } from '../../services/api';
import { useCountUp } from '../../hooks/useCountUp';
import { Skeleton } from '../ui/Skeleton';

interface Counts {
  total: number;
  complete: number;
  in_progress: number;
  flagged: number;
  failed: number;
  /* Per-stage: how many projects have each stage in 'complete'
     status (vs anything else). */
  dd_complete: number;
  spa_complete: number;
  risk_complete: number;
}

const ZERO: Counts = {
  total: 0,
  complete: 0,
  in_progress: 0,
  flagged: 0,
  failed: 0,
  dd_complete: 0,
  spa_complete: 0,
  risk_complete: 0,
};

interface DonutProps {
  value: number;
  total: number;
  size?: number;
  thickness?: number;
  color: string;
  centerLabel: string;
  centerSub?: string;
}

function Donut({
  value,
  total,
  size = 116,
  thickness = 12,
  color,
  centerLabel,
  centerSub,
}: DonutProps): JSX.Element {
  const r = (size - thickness) / 2;
  const c = 2 * Math.PI * r;
  const pct = total > 0 ? Math.min(1, value / total) : 0;
  const dash = c * pct;
  const gap = c - dash;
  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      aria-label={`${value} of ${total}`}
      style={{ display: 'block' }}
    >
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke="var(--lines)"
        strokeWidth={thickness}
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke={color}
        strokeWidth={thickness}
        strokeDasharray={`${dash} ${gap}`}
        strokeDashoffset={c * 0.25}
        strokeLinecap="round"
      />
      <text
        x="50%"
        y="46%"
        textAnchor="middle"
        style={{
          fontFamily: 'var(--font-display)',
          fontSize: '1.6rem',
          fontWeight: 600,
          fill: 'var(--text)',
        }}
      >
        {centerLabel}
      </text>
      {centerSub && (
        <text
          x="50%"
          y="62%"
          textAnchor="middle"
          style={{
            fontSize: '0.7rem',
            fill: 'var(--muted)',
          }}
        >
          {centerSub}
        </text>
      )}
    </svg>
  );
}

export function KpiDonuts(): JSX.Element | null {
  const navigate = useNavigate();
  const [counts, setCounts] = useState<Counts>(ZERO);
  const [loaded, setLoaded] = useState(false);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    void load();

    async function load(): Promise<void> {
      try {
        const all = await listProjects({ limit: 100 });
        // Enrich each project so we can count per-stage complete.
        const details = await Promise.all(
          all.projects.map((p) =>
            getProject(p.audit_id).catch(() => null),
          ),
        );
        let dd_complete = 0;
        let spa_complete = 0;
        let risk_complete = 0;
        const counts: Counts = { ...ZERO, total: all.total };
        for (let i = 0; i < all.projects.length; i++) {
          const p = all.projects[i];
          counts[p.overall_status as keyof Counts] =
            (counts[p.overall_status as keyof Counts] || 0) + 1;
          const d = details[i];
          if (d?.dd_assessment?.status === 'complete') dd_complete++;
          if (d?.spa_assessment?.status === 'complete') spa_complete++;
          if (d?.risk_assessment?.status === 'complete') risk_complete++;
        }
        counts.dd_complete = dd_complete;
        counts.spa_complete = spa_complete;
        counts.risk_complete = risk_complete;
        setCounts(counts);
        setLoaded(true);
      } catch {
        setHidden(true);
      }
    }
  }, []);

  if (hidden) return null;
  if (!loaded) {
    // Skeleton row mirroring the final KPI grid (title · donut · footer).
    return (
      <section
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: 'var(--s4)',
          marginBottom: 'var(--s4)',
          minWidth: 0,
          width: '100%',
        }}
        aria-label="Loading project KPIs"
        aria-busy="true"
      >
        {[0, 1, 2, 3].map((i) => (
          <div
            key={i}
            className="card kpi-donut-card"
            style={{
              padding: 'var(--s4)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 'var(--s3)',
            }}
          >
            <Skeleton width="55%" height={12} />
            <Skeleton width={116} height={116} radius="50%" />
            <Skeleton width="80%" height={12} />
          </div>
        ))}
      </section>
    );
  }

  if (counts.total === 0) {
    return null; // ProjectStatusDistribution shows the empty-state CTA.
  }

  return (
    <section
      style={{
        display: 'grid',
        /* Sprint 5 client feedback 2026-06-22 (Second-Set FE Issue 3):
           replace fixed repeat(4,1fr) with auto-fit so the row wraps
           into 2 / 1 columns on narrower viewports rather than
           overflowing the scaffold. minWidth:0 + width:100% prevent
           the donuts from pushing horizontal scroll on the page. */
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        gap: 'var(--s4)',
        marginBottom: 'var(--s4)',
        minWidth: 0,
        width: '100%',
      }}
      aria-label="Project KPIs"
    >
      <KpiCard
        title="Overall Completion"
        value={counts.complete}
        total={counts.total}
        color="var(--pass)"
        format={(v) => `${counts.total ? Math.round((v / counts.total) * 100) : 0}%`}
        formatSub={(v) => `${Math.round(v)}/${counts.total}`}
        footer={`${counts.complete} of ${counts.total} audits complete`}
        onClick={() => navigate('/projects?status=complete')}
      />
      <KpiCard
        title="Stage 1 — DD"
        value={counts.dd_complete}
        total={counts.total}
        color="var(--dd-accent)"
        format={(v) => `${Math.round(v)}`}
        formatSub={() => `of ${counts.total}`}
        footer="Stage 1 reports finished"
        onClick={() => navigate('/projects')}
      />
      <KpiCard
        title="Stage 2 — SPA"
        value={counts.spa_complete}
        total={counts.total}
        color="var(--spa-accent)"
        format={(v) => `${Math.round(v)}`}
        formatSub={() => `of ${counts.total}`}
        footer="Legal review finished"
        onClick={() => navigate('/projects')}
      />
      {/* Sprint 5 client feedback 2026-07-11 (Iter4d) — Stage 3 card
          title switches from "Stage 3 — Risk" to "Stage 3 — Report"
          to match the parallel rename of the Risk Report surface to
          Draft Report. Value + colour + navigation unchanged. */}
      <KpiCard
        title="Stage 3 — Report"
        value={counts.risk_complete}
        total={counts.total}
        color="var(--risk-accent)"
        format={(v) => `${Math.round(v)}`}
        formatSub={() => `of ${counts.total}`}
        footer="Final report issued"
        onClick={() => navigate('/projects')}
      />
    </section>
  );
}

function KpiCard({
  title,
  value,
  total,
  color,
  format,
  formatSub,
  footer,
  onClick,
}: {
  title: string;
  value: number;
  total: number;
  color: string;
  format: (v: number) => string;
  formatSub?: (v: number) => string;
  footer: string;
  onClick: () => void;
}): JSX.Element {
  // Count-up drives BOTH the centre number and the donut arc (the arc's
  // dash is derived from `value`), so the ring draws in as the figure ticks.
  const shown = useCountUp(value, 1200);
  return (
    <article
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => (e.key === 'Enter' ? onClick() : undefined)}
      className="card kpi-donut-card"
      style={{
        padding: 'var(--s4) var(--s4)',
        textAlign: 'center',
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 'var(--s2)',
      }}
    >
      <h3
        style={{
          margin: 0,
          fontFamily: 'var(--font-body, inherit)',
          fontSize: '0.75rem',
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          color: 'var(--muted)',
        }}
      >
        {title}
      </h3>
      <Donut
        value={shown}
        total={total}
        color={color}
        centerLabel={format(shown)}
        centerSub={formatSub ? formatSub(shown) : undefined}
      />
      <p style={{ margin: 0, fontSize: '0.8rem', color: 'var(--text-sec)' }}>
        {footer}
      </p>
    </article>
  );
}
